/**
 * 低多边形网格建造器。用盒子、柱体、锥台拼出单位与建筑。
 */

import { ident, mulXf, xfDir, xfPoint, type Xf } from './math.ts';

export class Mesh {
  readonly px: number[] = [];
  readonly py: number[] = [];
  readonly pz: number[] = [];
  readonly nx: number[] = [];
  readonly ny: number[] = [];
  readonly nz: number[] = [];
  readonly cr: number[] = [];
  readonly cg: number[] = [];
  readonly cb: number[] = [];
  readonly idx: number[] = [];

  get vertCount(): number {
    return this.px.length;
  }

  get triCount(): number {
    return (this.idx.length / 3) | 0;
  }
}

export class MeshBuilder {
  readonly mesh = new Mesh();
  private stack: Xf[] = [ident()];

  private get xf(): Xf {
    return this.stack[this.stack.length - 1]!;
  }

  push(): this {
    this.stack.push({ ...this.xf });
    return this;
  }

  pop(): this {
    if (this.stack.length > 1) this.stack.pop();
    return this;
  }

  mul(m: Xf): this {
    this.stack[this.stack.length - 1] = mulXf(this.xf, m);
    return this;
  }

  move(x: number, y: number, z: number): this {
    const m = ident();
    m.tx = x;
    m.ty = y;
    m.tz = z;
    return this.mul(m);
  }

  yaw(a: number): this {
    const c = Math.cos(a);
    const s = Math.sin(a);
    const m = ident();
    m.xx = c;
    m.xz = s;
    m.zx = -s;
    m.zz = c;
    return this.mul(m);
  }

  pitch(a: number): this {
    const c = Math.cos(a);
    const s = Math.sin(a);
    const m = ident();
    m.yy = c;
    m.yz = -s;
    m.zy = s;
    m.zz = c;
    return this.mul(m);
  }

  roll(a: number): this {
    const c = Math.cos(a);
    const s = Math.sin(a);
    const m = ident();
    m.xx = c;
    m.xy = s;
    m.yx = -s;
    m.yy = c;
    return this.mul(m);
  }

  scale(x: number, y: number, z: number): this {
    const m = ident();
    m.xx = x;
    m.yy = y;
    m.zz = z;
    return this.mul(m);
  }

  /** 顶点色：0xRRGGBB */
  private rgb(hex: number): [number, number, number] {
    return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
  }

  private vert(x: number, y: number, z: number, nx: number, ny: number, nz: number, hex: number): number {
    const p = xfPoint(this.xf, x, y, z);
    const n = xfDir(this.xf, nx, ny, nz);
    const len = Math.hypot(n.x, n.y, n.z) || 1;
    const [r, g, b] = this.rgb(hex);
    const m = this.mesh;
    const i = m.px.length;
    m.px.push(p.x);
    m.py.push(p.y);
    m.pz.push(p.z);
    m.nx.push(n.x / len);
    m.ny.push(n.y / len);
    m.nz.push(n.z / len);
    m.cr.push(r);
    m.cg.push(g);
    m.cb.push(b);
    return i;
  }

  tri(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number,
    hex: number,
  ): this {
    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const i0 = this.vert(ax, ay, az, nx, ny, nz, hex);
    const i1 = this.vert(bx, by, bz, nx, ny, nz, hex);
    const i2 = this.vert(cx, cy, cz, nx, ny, nz, hex);
    this.mesh.idx.push(i0, i1, i2);
    return this;
  }

  /** 单面四边形，法线由三点叉积决定。绕序：右手法则朝外。 */
  quad(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number,
    dx: number,
    dy: number,
    dz: number,
    hex: number,
  ): this {
    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = dx - ax;
    const vy = dy - ay;
    const vz = dz - az;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const i0 = this.vert(ax, ay, az, nx, ny, nz, hex);
    const i1 = this.vert(bx, by, bz, nx, ny, nz, hex);
    const i2 = this.vert(cx, cy, cz, nx, ny, nz, hex);
    const i3 = this.vert(dx, dy, dz, nx, ny, nz, hex);
    this.mesh.idx.push(i0, i1, i2, i0, i2, i3);
    return this;
  }

  /**
   * 轴对齐盒子。尺寸为全宽，底面中心默认在原点、沿 +Y 长高。
   * 若 `centered` 为 true，则盒子在三轴上都以原点为中心。
   */
  box(sx: number, sy: number, sz: number, hex: number, centered = false): this {
    const x0 = -sx / 2;
    const x1 = sx / 2;
    const y0 = centered ? -sy / 2 : 0;
    const y1 = centered ? sy / 2 : sy;
    const z0 = -sz / 2;
    const z1 = sz / 2;
    // +Y 顶 / -Y 底 / +Z 前 / -Z 后 / +X 右 / -X 左
    this.quad(x0, y1, z1, x1, y1, z1, x1, y1, z0, x0, y1, z0, hex);
    this.quad(x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1, hex);
    this.quad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, hex);
    this.quad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, hex);
    this.quad(x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1, hex);
    this.quad(x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0, hex);
    return this;
  }

  /** 竖直圆柱 / 圆台，底面圆心在原点。 */
  cylinder(r0: number, r1: number, h: number, hex: number, segs = 10, caps = true): this {
    const n = Math.max(6, segs);
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * Math.PI * 2;
      const a1 = ((i + 1) / n) * Math.PI * 2;
      const c0 = Math.cos(a0);
      const s0 = Math.sin(a0);
      const c1 = Math.cos(a1);
      const s1 = Math.sin(a1);
      this.quad(r0 * c0, 0, r0 * s0, r0 * c1, 0, r0 * s1, r1 * c1, h, r1 * s1, r1 * c0, h, r1 * s0, hex);
      if (caps) {
        if (r0 > 0.001) this.tri(0, 0, 0, r0 * c1, 0, r0 * s1, r0 * c0, 0, r0 * s0, hex);
        if (r1 > 0.001) this.tri(0, h, 0, r1 * c0, h, r1 * s0, r1 * c1, h, r1 * s1, hex);
      }
    }
    return this;
  }

  /** 沿 Z 轴的水平罐体（精炼厂储罐） */
  tankZ(r: number, len: number, hex: number, segs = 8): this {
    this.push();
    this.pitch(-Math.PI / 2);
    this.move(0, -len / 2, 0);
    this.cylinder(r, r, len, hex, segs);
    this.pop();
    return this;
  }

  /** 三角楔：沿 Z 的坡屋顶 */
  wedge(sx: number, sy: number, sz: number, hex: number): this {
    const x0 = -sx / 2;
    const x1 = sx / 2;
    const z0 = -sz / 2;
    const z1 = sz / 2;
    // 两坡
    this.quad(x0, 0, z1, 0, sy, z1, 0, sy, z0, x0, 0, z0, hex);
    this.quad(0, sy, z1, x1, 0, z1, x1, 0, z0, 0, sy, z0, hex);
    // 山墙
    this.quad(x0, 0, z1, x1, 0, z1, 0, sy, z1, x0, 0, z1, hex);
    this.quad(x1, 0, z0, x0, 0, z0, 0, sy, z0, x1, 0, z0, hex);
    return this;
  }
}
